import { callLoader } from "@/redux/Loader/actions";
import {
    CLEAR_USER_LIST,
    USER_DROPDOWN_BY_COMPANY_N_ROLE,
    USER_LIST,
    USER_PAGINATION,
} from "./constant";
import { APICall } from "@/config/NetworkUtility";
import { getErrorPayload } from "@/utils/reduxError";

// ---- simple in-memory TTL cache (module-scoped) ----
const DEFAULT_TTL = 5 * 60 * 1000; // 5 min
const cache = new Map(); // key -> { data, expiry }

const getCached = (key) => {
    const entry = cache.get(key);
    if (!entry) return null;
    if (Date.now() > entry.expiry) {
        cache.delete(key);
        return null;
    }
    return entry.data;
};

const setCached = (key, data, ttl = DEFAULT_TTL) => {
    cache.set(key, { data, expiry: Date.now() + ttl });
};

const invalidateCache = (prefix) => {
    for (const key of cache.keys()) {
        if (!prefix || key.startsWith(prefix)) cache.delete(key);
    }
};

/**
 * Fetch paginated user list (cached per query-string, 5 min TTL)
 */
export const getUserList = (payload, { forceRefresh = false } = {}) => {
    return async (dispatch) => {
        const queryParams = new URLSearchParams(payload).toString();
        const cacheKey = `users:${queryParams}`;

        if (!forceRefresh) {
            const cached = getCached(cacheKey);
            if (cached) {
                dispatch({ type: USER_LIST, payload: cached.data });
                dispatch({ type: USER_PAGINATION, payload: cached.pagination });
                return cached.data;
            }
        }

        try {
            dispatch(callLoader(true));

            const url = `v1/Users/GetUserList?${queryParams}`;
            const result = await APICall("get", payload, url);

            if (result?.statuscode === 200 || result?.statuscode === 201) {
                const data = result?.data || [];
                const pagination = result?.pagination || {};

                setCached(cacheKey, { data, pagination });

                dispatch({ type: USER_LIST, payload: data });
                dispatch({ type: USER_PAGINATION, payload: pagination });

                return data;
            }

            throw new Error("Failed to fetch users");
        } catch (error) {
            throw error;
        } finally {
            dispatch(callLoader(false));
        }
    };
};

/**
 * Fetch single user by ID
 */
// export const getUserByID = (payload) => {
//     return dispatch => {
//         try {
//             dispatch(callLoader(true));
//             const url = `v1/Users/GetUserByID?UserId=${payload}`;
//             return APICall('get', payload, url).then(result => {
//                 if (result?.statuscode === 200) {
//                     return result?.data;
//                 } else {
//                     return Promise.reject();
//                 }
//             }, error => {
//                 return Promise.reject(error);
//             });
//         } catch (error) {
//             console.log(error);
//             return Promise.reject(error);
//         } finally {
//             dispatch(callLoader(false));
//         }
//     };
// };

/**
 * Create or update a user via SaveUserInfo
 */
export const saveUserInfo = (payload) => {
    return (dispatch) => {
        try {
            dispatch(callLoader(true));
            const url = `v1/Users/InsertUserInfo`;
            return APICall("post", payload, url).then(
                (result) => {
                    dispatch(callLoader(false));
                    if (result?.statuscode === 200 || result?.statuscode === 201) {
                        invalidateCache("users:");
                        invalidateCache("dropdown:");
                        return result;
                    } else {
                        return Promise.reject(result);
                    }
                },
                (error) => {
                    dispatch(callLoader(false));
                    return Promise.reject(error);
                },
            );
        } catch (error) {
            console.log(error);
            dispatch(callLoader(false));
            return Promise.reject(error);
        } finally {
            dispatch(callLoader(false));
        }
    };
};

export const UpdateUserInfo = (payload) => {
    return (dispatch) => {
        try {
            dispatch(callLoader(true));
            const url = `v1/Users/UpdateUserInfo`;
            return APICall("post", payload, url).then(
                (result) => {
                    dispatch(callLoader(false));
                    if (result?.statuscode === 200 || result?.statuscode === 201) {
                        invalidateCache("users:");
                        invalidateCache("dropdown:");
                        return result;
                    } else {
                        return Promise.reject(result);
                    }
                },
                (error) => {
                    dispatch(callLoader(false));
                    return Promise.reject(error);
                },
            );
        } catch (error) {
            dispatch(callLoader(false));
            return Promise.reject(error);
        } finally {
            dispatch(callLoader(false));
        }
    };
};

/**
 * Fetch user dropdown list by company & role (cached per query-string, 10 min TTL)
 */
export const getUserDropdownList = (payload, { forceRefresh = false } = {}) => {
    return async (dispatch) => {
        const queryParams = new URLSearchParams(payload).toString();
        const cacheKey = `dropdown:${queryParams}`;

        if (!forceRefresh) {
            const cached = getCached(cacheKey);
            if (cached) {
                dispatch({ type: USER_DROPDOWN_BY_COMPANY_N_ROLE, payload: cached });
                return { data: cached };
            }
        }

        dispatch(callLoader(true));

        try {
            const response = await APICall(
                "get",
                {},
                `v1/CompanySetup/GetUserDropdownListByCompanyAndRole?${queryParams}`,
            );

            if ([200, 201].includes(response?.statuscode)) {
                const data = response.data ?? [];

                setCached(cacheKey, data, 10 * 60 * 1000);

                dispatch({
                    type: USER_DROPDOWN_BY_COMPANY_N_ROLE,
                    payload: data,
                });
                return response;
            }

            return Promise.reject({
                status: response?.statuscode,
                message: response?.message ?? "Failed to load user list.",
                data: response?.data ?? null,
            });
        } catch (error) {
            return Promise.reject(getErrorPayload(error));
        } finally {
            dispatch(callLoader(false));
        }
    };
};

export const clearUserList = () => {
    invalidateCache("users:");
    return { type: CLEAR_USER_LIST };
};